import React, { useState } from 'react';
import axios from 'axios';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent } from '../../components/ui/card';
import { toast } from '../../hooks/use-toast';
import { API, authHeaders, ConfirmDialog, blankClient } from './utils';

const ClientsTable = ({ clients, reload }) => {
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blankClient);
  const [busy, setBusy] = useState(false);

  const startNew = () => { setEditing('new'); setForm(blankClient); };
  const startEdit = (c) => { setEditing(c.id); setForm({ ...blankClient, ...c }); };

  const save = async () => {
    if (!form.name.trim()) {
      toast({ title: 'Name is required', variant: 'destructive' });
      return;
    }
    setBusy(true);
    try {
      if (editing === 'new') {
        await axios.post(`${API}/api/invoices/clients`, form, { headers: authHeaders() });
        toast({ title: 'Client added' });
      } else {
        await axios.patch(`${API}/api/invoices/clients/${editing}`, form, { headers: authHeaders() });
        toast({ title: 'Client updated' });
      }
      setEditing(null);
      if (reload) await reload();
    } catch (e) {
      toast({
        title: 'Save failed',
        description: e?.response?.data?.detail || '',
        variant: 'destructive',
      });
    } finally { setBusy(false); }
  };

  const [pendingClient, setPendingClient] = useState(null);
  const remove = (c) => setPendingClient(c);
  const confirmRemove = async () => {
    const c = pendingClient;
    setPendingClient(null);
    if (!c) return;
    try {
      await axios.delete(`${API}/api/invoices/clients/${c.id}`, { headers: authHeaders() });
      if (reload) await reload();
      toast({ title: 'Client deleted' });
    } catch (e) {
      toast({ title: 'Delete failed', description: e?.response?.data?.detail || '', variant: 'destructive' });
    }
  };

  return (
    <Card>
      <CardContent className="p-4 space-y-3">
        <div className="flex justify-between items-center">
          <p className="text-sm text-gray-500">Reusable client records — pick when creating an invoice.</p>
          <Button onClick={startNew} size="sm" className="bg-rose-600 hover:bg-rose-700" data-testid="add-client-btn">
            <Plus className="h-4 w-4 mr-1" /> Add Client
          </Button>
        </div>

        {clients.length === 0 && editing !== 'new' && (
          <div className="py-8 text-center text-gray-400 text-sm">No clients saved yet.</div>
        )}

        {clients.map(c => (
          <div
            key={c.id}
            className="border rounded-lg p-3"
            data-testid={`client-row-${c.id}`}
          >
            {editing === c.id ? (
              <ClientForm form={form} setForm={setForm} onCancel={() => setEditing(null)} onSave={save} busy={busy} />
            ) : (
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-900">{c.name}</p>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-[11px] text-gray-500 mt-1">
                    {c.contact_person && <p>👤 {c.contact_person}</p>}
                    {c.email && <p>✉ {c.email}</p>}
                    {c.phone && <p>📞 {c.phone}</p>}
                    {c.trn && <p>TRN: {c.trn}</p>}
                  </div>
                  {c.address && <p className="text-[11px] text-gray-500 mt-0.5 whitespace-pre-line">📍 {c.address}</p>}
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" onClick={() => startEdit(c)}>Edit</Button>
                  <Button size="sm" variant="ghost" className="text-rose-600 hover:bg-rose-50" onClick={() => remove(c)} data-testid={`delete-client-${c.id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        ))}

        {editing === 'new' && (
          <div className="border-2 border-dashed border-rose-200 rounded-lg p-3 bg-rose-50/30">
            <ClientForm form={form} setForm={setForm} onCancel={() => setEditing(null)} onSave={save} busy={busy} />
          </div>
        )}
        <ConfirmDialog
          open={!!pendingClient}
          onOpenChange={(v) => { if (!v) setPendingClient(null); }}
          title={`Delete client "${pendingClient?.name || ''}"?`}
          description="This cannot be undone."
          onConfirm={confirmRemove}
          testid="delete-client-dialog"
        />
      </CardContent>
    </Card>
  );
};

const ClientForm = ({ form, setForm, onCancel, onSave, busy }) => (
  <div className="space-y-2">
    <div className="grid sm:grid-cols-2 gap-2">
      <div>
        <Label className="text-xs">Name *</Label>
        <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Acme Corp LLC" data-testid="client-form-name" />
      </div>
      <div>
        <Label className="text-xs">Contact Person</Label>
        <Input value={form.contact_person} onChange={e => setForm({ ...form, contact_person: e.target.value })} />
      </div>
      <div>
        <Label className="text-xs">Email</Label>
        <Input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
      </div>
      <div>
        <Label className="text-xs">Phone</Label>
        <Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
      </div>
      <div className="sm:col-span-2">
        <Label className="text-xs">Address</Label>
        <Textarea rows={2} value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} />
      </div>
      <div>
        <Label className="text-xs">TRN (Tax Reg. #)</Label>
        <Input value={form.trn} onChange={e => setForm({ ...form, trn: e.target.value })} />
      </div>
    </div>
    <div className="flex justify-end gap-2">
      <Button variant="outline" size="sm" onClick={onCancel}>Cancel</Button>
      <Button size="sm" onClick={onSave} disabled={busy} className="bg-rose-600 hover:bg-rose-700" data-testid="client-form-save">
        {busy ? 'Saving…' : 'Save'}
      </Button>
    </div>
  </div>
);

export default ClientsTable;
